import { useContext, useMemo } from 'react';
import ReportsContext from '../../../contexts/ReportsContext';
import ScorecardsContext from '../../../contexts/ScorecardsContext';
import GoalsContext from '../../../contexts/GoalsContext';
import DashboardsContext from '../../../contexts/DashboardsContext';
import useHasAccess from '../../../hooks/useHasAccess';
import BoardsContext from '../../../contexts/BoardsContext';

const isReport = (
  item: ContentIndexTypes.ContentItem,
): item is ContentIndexTypes.ReportContentItem => item.type === 'report';
const isDashboard = (
  item: ContentIndexTypes.ContentItem,
): item is ContentIndexTypes.DashboardContentItem => item.type === 'dashboard';
const isScorecard = (
  item: ContentIndexTypes.ContentItem,
): item is ContentIndexTypes.ScorecardContentItem => item.type === 'scorecard';
const isGoal = (
  item: ContentIndexTypes.ContentItem,
): item is ContentIndexTypes.GoalContentItem => item.type === 'goal';
const isBoard = (
  item: ContentIndexTypes.BoardContentItem,
): item is ContentIndexTypes.BoardContentItem => item.type === 'board';

export const contentTypeCheckers = {
  isReport,
  isDashboard,
  isScorecard,
  isGoal,
  isBoard,
};

const sortByName = (
  a: ContentIndexTypes.ContentItem,
  b: ContentIndexTypes.ContentItem,
) => {
  const aName = a.name.toLowerCase();
  const bName = b.name.toLowerCase();
  if (aName > bName) return 1;
  if (aName < bName) return -1;
  return 0;
};

const useContent = () => {
  const { allReports, archivedReports } = useContext(ReportsContext);
  const { allDashboards, archivedDashboards } = useContext(DashboardsContext);
  const { scorecards: allScorecards, archivedScorecards } =
    useContext(ScorecardsContext);
  const { goals: allGoals, archivedGoals } = useContext(GoalsContext);
  const { boards: allBoards, archivedBoards } = useContext(BoardsContext);
  const hasAccess = useHasAccess();

  const contentItems = useMemo((): ContentIndexTypes.ContentItem[] => {
    const reports = allReports
      .filter((r) =>
        hasAccess({
          access: r.access,
          resource: r,
          typeId: r.id,
          type: 'report',
        }),
      )
      .map(
        (r): ContentIndexTypes.ReportContentItem => ({
          type: 'report',
          name: r.name,
          report: r,
          key: r.id,
          isPrivate: !r.isV3Published,
          isPublic: r.v3Access.isPublic,
          isHidden: false,
          labelIds: r.labelIds,
        }),
      );
    const dashboards = allDashboards
      .filter((d) =>
        hasAccess({
          access: d.access,
          resource: d,
          typeId: d.id,
          type: 'dashboard',
        }),
      )
      .map(
        (d): ContentIndexTypes.DashboardContentItem => ({
          type: 'dashboard',
          name: d.name,
          dashboard: d,
          key: d.id,
          isPrivate: !d.isV3Published,
          isPublic: d.v3Access.isPublic,
          isHidden: false,
          labelIds: d.labelIds,
        }),
      );
    const scorecards = allScorecards
      .filter((s) =>
        hasAccess({
          access: s.access,
          resource: s,
          typeId: s.id,
          type: 'scorecard',
        }),
      )
      .map(
        (s): ContentIndexTypes.ScorecardContentItem => ({
          type: 'scorecard',
          name: s.title,
          scorecard: s,
          key: s.id,
          isPrivate: !s.isV3Published,
          isPublic: s.v3Access.isPublic,
          isHidden: false,
          labelIds: s.labelIds,
        }),
      );
    const goals = allGoals
      .filter((g) =>
        hasAccess({
          access: g.access,
          resource: g,
          typeId: g.id,
          type: 'goal',
        }),
      )
      .map(
        (g): ContentIndexTypes.GoalContentItem => ({
          type: 'goal',
          name: g.title,
          goal: g,
          key: g.id,
          isPrivate: !g.isV3Published,
          isPublic: g.v3Access.isPublic,
          isHidden: false,
          labelIds: g.labelIds,
        }),
      );
    const boards = allBoards
      .filter(
        (board) =>
          board.isAsset &&
          hasAccess({ resource: board, typeId: board.id, type: 'board' }),
      )
      .map(
        (board): ContentIndexTypes.BoardContentItem => ({
          type: 'board',
          name: board.name,
          board,
          key: board.id,
          isPrivate: !board.isV3Published,
          isPublic: board.v3Access.isPublic,
          isHidden: false,
          labelIds: board.labelIds,
        }),
      );

    const archivedDashboardItems = archivedDashboards.map(
      (d): ContentIndexTypes.DashboardContentItem => ({
        type: 'dashboard',
        name: d.name,
        dashboard: d,
        key: `archived-${d.id}`,
        isPrivate: !d.isV3Published,
        isPublic: d.v3Access.isPublic,
        isHidden: true,
        labelIds: d.labelIds,
      }),
    );
    const archivedReportItems = archivedReports.map(
      (r): ContentIndexTypes.ReportContentItem => ({
        type: 'report',
        name: r.name,
        report: r,
        key: `archived-${r.id}`,
        isPrivate: !r.isV3Published,
        isPublic: r.v3Access.isPublic,
        isHidden: true,
        labelIds: r.labelIds,
      }),
    );
    const archivedScorecardItems = archivedScorecards.map(
      (s): ContentIndexTypes.ScorecardContentItem => ({
        type: 'scorecard',
        name: s.title,
        scorecard: s,
        key: `archived-${s.id}`,
        isPrivate: !s.isV3Published,
        isPublic: s.v3Access.isPublic,
        isHidden: true,
        labelIds: s.labelIds,
      }),
    );
    const archivedGoalItems = archivedGoals.map(
      (g): ContentIndexTypes.GoalContentItem => ({
        type: 'goal',
        name: g.title,
        goal: g,
        key: `archived-${g.id}`,
        isPrivate: !g.isV3Published,
        isPublic: g.v3Access.isPublic,
        isHidden: true,
        labelIds: g.labelIds,
      }),
    );
    const archivedBoardItems = archivedBoards
      .filter((board) => board.isAsset)
      .map(
        (board): ContentIndexTypes.BoardContentItem => ({
          type: 'board',
          name: board.name,
          board,
          key: `archived-${board.id}`,
          isPrivate: !board.isV3Published,
          isPublic: board.v3Access.isPublic,
          isHidden: true,
          labelIds: board.labelIds,
        }),
      );

    const unsorted: ContentIndexTypes.ContentItem[] = [
      ...reports,
      ...dashboards,
      ...scorecards,
      ...goals,
      ...boards,
      ...archivedDashboardItems,
      ...archivedReportItems,
      ...archivedScorecardItems,
      ...archivedGoalItems,
      ...archivedBoardItems,
    ];

    return unsorted.sort(sortByName);
  }, [
    allBoards,
    allDashboards,
    allGoals,
    allReports,
    allScorecards,
    archivedBoards,
    archivedDashboards,
    archivedGoals,
    archivedReports,
    archivedScorecards,
    hasAccess,
  ]);

  return contentItems;
};

export default useContent;
